import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { RankingList } from "../../../../components/RankingList";
import { useResto } from "../../../../hooks/useResto";
import { useToast } from "../../../../context/ToastContext";
import { approveWorker, getPendingWorkersByPlaceId, rejectWorker } from "../../../../services/workersService";
import { getTenantId } from "../../../../utils/tenant";
import TwoColumns from "../../../../components/TwoColumns";
import { RightBar } from "../../../../components/RightBar";
import BannerDesktop from "../../../../components/BannerDesktop";
import { useDeviceType } from "../../../../hooks/useMediaQuery";

const PageWrapper = styled.div`
  width: 100%;
  max-width: ${({ $embedded }) => ($embedded ? "900px" : "100%")};
  margin: 0 auto;
  margin-top: ${({ $embedded }) => ($embedded ? "24px" : "0")};
  box-sizing: border-box;
  overflow-x: auto;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  border: 1px solid ${({ $variant }) => ($variant === "ghost" ? "#fca5a5" : "#ff0102")};
  background: ${({ $variant }) => ($variant === "ghost" ? "#fff" : "#ff0102")};
  color: ${({ $variant }) => ($variant === "ghost" ? "#b91c1c" : "#fff")};
  padding: 6px 10px;
  font-size: 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  background: #fff;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 28px 20px;
  text-align: center;
  color: #6b7280;
`;

const EmptyStateTitle = styled.p`
  margin: 0 0 6px;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
`;

const EmptyStateText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
`;

export const PendingApprovalsTab = ({ embedded = false, onPendingCountChange }) => {
  const toast = useToast();
  const { resto, getResto } = useResto();
  const { isDesktop } = useDeviceType();
  const [restoName, setRestoName] = useState("");
  const [restoAddress, setRestoAddress] = useState("");
  const [avatar, setAvatar] = useState("");
  const [restoId, setRestoId] = useState("");
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const isMountedRef = useRef(true);

  const placeId = useMemo(() => {
    return resto?.place_id || resto?.placeId || resto?._id || null;
  }, [resto]);

  const loadPending = useCallback(async () => {
    if (!placeId) {
      setPending([]);
      return;
    }
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const tenantId = getTenantId();
      const res = await getPendingWorkersByPlaceId(placeId, tenantId);
      const list = Array.isArray(res) ? res : res?.data || [];
      if (isMountedRef.current) {
        setPending(list);
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast?.error?.("No se pudieron cargar los pendientes.");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [placeId, toast]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!resto) getResto();
  }, [resto, getResto]);

  useEffect(() => {
    if (resto) {
      setRestoId(resto?._id || "");
      setRestoName(resto?.name || "");
      setRestoAddress(resto?.address || "");
      setAvatar(resto?.brand || "");
    }
  }, [resto]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    onPendingCountChange?.(pending.length);
  }, [onPendingCountChange, pending.length]);

  const markBusy = (id, value) => {
    if (!isMountedRef.current) return;
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (value) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleApprove = async (row) => {
    const id = row?.id || row?._id;
    if (!id) return;
    markBusy(id, true);
    try {
      const tenantId = getTenantId();
      await approveWorker(id, tenantId);
      if (isMountedRef.current) {
        setPending((prev) => prev.filter((item) => (item?.id || item?._id) !== id));
        toast?.success?.("Trabajador aprobado.");
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast?.error?.("No se pudo aprobar.");
      }
    } finally {
      markBusy(id, false);
    }
  };

  const handleReject = async (row) => {
    const id = row?.id || row?._id;
    if (!id) return;
    markBusy(id, true);
    try {
      await rejectWorker(id);
      if (isMountedRef.current) {
        setPending((prev) => prev.filter((item) => (item?.id || item?._id) !== id));
        toast?.success?.("Trabajador rechazado.");
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast?.error?.("No se pudo rechazar.");
      }
    } finally {
      markBusy(id, false);
    }
  };

  const headers = [
    { key: "name", label: "Nombre", type: "avatar" },
    { key: "email", label: "Email" },
    {
      key: "createdAt",
      label: "Registro",
      render: (row) => {
        if (!row?.createdAt) return "-";
        const date = new Date(row.createdAt);
        return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
      },
    },
  ];

  const actions = [
    {
      render: (row) => {
        const id = row?.id || row?._id;
        const isBusy = id ? busyIds.has(id) : false;
        return (
          <ActionButtons>
            <ActionButton type="button" disabled={isBusy} onClick={() => handleApprove(row)}>
              Aprobar
            </ActionButton>
            <ActionButton type="button" $variant="ghost" disabled={isBusy} onClick={() => handleReject(row)}>
              Rechazar
            </ActionButton>
          </ActionButtons>
        );
      },
    },
  ];

  const content = (
    <PageWrapper $embedded={embedded}>
      <RankingList
        title={`Pendientes de aprobar (${pending.length})`}
        headers={headers}
        data={pending}
        actions={actions}
        loading={loading}
        getRowKey={(row, i) => row?.id || row?._id || i}
        emptyMessage=""
      />

      {!loading && pending.length === 0 && (
        <EmptyState>
          <EmptyStateTitle>Pendientes de aprobar (0)</EmptyStateTitle>
          <EmptyStateText>
            No tienes solicitudes pendientes por revisar en este momento.
          </EmptyStateText>
        </EmptyState>
      )}

      {!embedded && <div style={{ height: 100 }}></div>}
    </PageWrapper>
  );

  if (!isDesktop || embedded) {
    return content;
  }

  return (
    <TwoColumns
      left={
        <>
          <BannerDesktop img={avatar} alt={restoName} />
          {content}
        </>
      }
      right={<RightBar restoName={restoName} restoAddress={restoAddress} restoId={restoId} />}
    />
  );
};
